import { useEffect, useRef, type KeyboardEvent } from 'react'
import { Bold, Italic, List, Underline } from 'lucide-react'
import { sanitizeRichTextHtml } from '@/lib/sanitizeRichTextHtml'

interface Props {
  name: string
  value: string
  onChange: (html: string) => void
  required?: boolean
  className?: string
}

export default function AgendaItemDescriptionEditor({ name, value, onChange, required, className }: Props) {
  const editorRef = useRef<HTMLDivElement>(null)
  const hiddenRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = sanitizeRichTextHtml(value || '')
    }
  }, [value])

  const sync = () => {
    if (!editorRef.current) return
    const sanitized = sanitizeRichTextHtml(editorRef.current.innerHTML)
    if (editorRef.current.innerHTML !== sanitized) editorRef.current.innerHTML = sanitized
    if (hiddenRef.current) hiddenRef.current.value = sanitized
    onChange(sanitized)
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter' || event.shiftKey) return
    event.preventDefault()
    document.execCommand('insertParagraph')
    sync()
  }

  const exec = (cmd: string, cmdValue?: string) => {
    document.execCommand(cmd, false, cmdValue)
    sync()
    editorRef.current?.focus()
  }

  return (
    <div className="space-y-0 overflow-hidden rounded-md border border-[#E8E8E5] bg-white">
      <div className="flex items-center gap-0.5 border-b border-[#E8E8E5] bg-[#F7F7F5] px-1.5 py-1">
        <button type="button" onClick={() => exec('bold')} className="rounded p-1 text-[#37352F] hover:bg-white" title="Bold">
          <Bold className="h-3.5 w-3.5" />
        </button>
        <button type="button" onClick={() => exec('italic')} className="rounded p-1 text-[#37352F] hover:bg-white" title="Italic">
          <Italic className="h-3.5 w-3.5" />
        </button>
        <button type="button" onClick={() => exec('underline')} className="rounded p-1 text-[#37352F] hover:bg-white" title="Underline">
          <Underline className="h-3.5 w-3.5" />
        </button>
        <button type="button" onClick={() => exec('insertUnorderedList')} className="rounded p-1 text-[#37352F] hover:bg-white" title="Bullet list">
          <List className="h-3.5 w-3.5" />
        </button>
      </div>
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={sync}
        onBlur={sync}
        onKeyDown={handleKeyDown}
        className={`w-full px-3 py-2 text-sm text-[#37352F] outline-none focus:ring-1 focus:ring-inset focus:ring-[#2383E2] [&_p]:m-0 [&_p+p]:mt-1 [&_ul]:list-disc [&_ul]:ps-5 [&_ol]:list-decimal [&_ol]:ps-5 ${className || 'min-h-[2.5rem]'}`}
      />
      <input ref={hiddenRef} type="hidden" name={name} defaultValue={value} required={required} />
    </div>
  )
}
